// Cliente de la Sala del HCE (plan Sala CRM↔HCE, rebanada 5) — ver packages/web/src/api/http-routes/hce-sala.ts y lib/sala-medico.ts.
//
// Reglas que este archivo respeta:
//   - el consultorio y el médico los decide el SERVIDOR por la sesión: aquí nunca se manda un id de médico ni de consultorio;
//   - toda acción lleva el estado que la pantalla VIO (`estadoEsperado`): si cambió, el servidor responde 409 y la pantalla vuelve a pedir la Sala;
//   - el tiempo de espera lo calcula el servidor (`esperaSeg`, con su reloj); el cliente solo suma el tiempo TRANSCURRIDO desde que recibió la
//     respuesta, medido con un reloj monotónico, nunca restando su propia hora de pared a la del servidor.
import { vaultUrl } from './hceVault';
import { cabecerasBoveda } from './audit/auditedFetch';
export type EstadoTurno = 'waiting' | 'called' | 'in_consult' | 'done' | 'no_show';

export type FilaSala = {
  id: number;
  turno: string | null;
  nombre: string;
  citaHora: string | null;
  motivo: string | null;
  estado: EstadoTurno;
  estadoMotivo: 'se_fue' | 'cierre_automatico' | 'no_llego' | 'cita_cancelada' | 'cita_reagendada' | null;
  llegadaAt: string;
  calledAt: string | null;
  esperaSeg: number | null;
  llamados: number;
  maxLlamados: number;
  cobro: 'sin_cobro' | 'orden_sin_consulta' | null;
  consultorio: string | null;
  modalidad: 'presencial' | 'telemedicina';
  ordenEnviada: boolean;
  /** El turno es de un día anterior y sigue abierto (cruzó la medianoche). */
  deOtroDia: boolean;
  consultaAbierta: boolean;
  sinEncounter: boolean;
  alerta: 'consulta_otro_medico' | 'otra_especialidad' | null;
  /** El turno espera (nadie lo llamó) y viene de una cita presencial suya: la fila ofrece «Deshacer asistencia» (el servidor revisa además factura, cobro y orden). Opcional: un servidor anterior no lo manda (entonces no se ofrece). */
  puedeDeshacer?: boolean;
  medico: { nombre: string | null; sinMedico: boolean };
  esMio: boolean;
};

/** Cita presencial de HOY de este médico que aún no llegó: la Sala le ofrece «Asistió». */
export type CitaPorLlegar = { citaId: string; nombre: string; citaHora: string | null; motivo: string | null };

export type SalaDelDia = {
  ahora: string;
  esperando: number;
  filas: FilaSala[];
  /** Citas de hoy de este médico sin llegada registrada (vacío en la vista de solo lectura del admin). Opcional: un servidor anterior a esta función no la manda. */
  citasPorLlegar?: CitaPorLlegar[];
  consultorio: { habitual: string | null; delDia: string | null; efectivo: string | null } | null;
  cobroDisponible: boolean;
  soloLectura: boolean;
  /** `false` = enfermería o vista de solo lectura: no se ofrece Iniciar ni Finalizar (el servidor respondería 403). */
  puedeConsultar: boolean;
};

export type ResumenSala = {
  ahora: string;
  esperando: number;
  enEspera: { id: number; nombre: string; turno: string | null; estado: 'waiting' | 'called'; llegadaAt: string }[];
};

export type ResultadoLlamar = { estado: 'called'; llamados: number; maxLlamados: number; consultorio: string | null; sinConsultorio: boolean };
export type ResultadoIniciar = {
  estado: 'in_consult';
  medplumPatientId: string | null;
  encounterId: string | null;
  encounterPendiente: boolean;
  motivoEncounter?: 'sin_medplum' | 'medplum_error' | 'paciente_no_sincronizado' | 'paciente_fusionado';
  resultado: 'propia' | 'toma' | 'otra_especialidad';
};
export type ResumenPendientes = { formulariosEnProgreso: number; alertas: string[]; total: number };
export type ResultadoFinalizar =
  | { requiereConfirmacion: true; resumen: ResumenPendientes; mensaje: string }
  | { estado: 'done'; encounterCerrado: boolean | null };
export type ResultadoIniciarPaciente =
  | { sinTurno: true }
  | { requiereConfirmacion: 'toma'; deMedico: string | null; especialidadDistinta: boolean }
  | { resultado: 'consulta_de_otro'; deMedico: string | null }
  | ResultadoIniciar;

/** Error del servidor con su estado HTTP, un código estable y, en un 409, el estado real del turno. */
export class ErrorSala extends Error {
  constructor(
    mensaje: string,
    readonly status: number,
    readonly codigo: string,
    readonly estadoActual?: EstadoTurno,
  ) {
    super(mensaje);
    this.name = 'ErrorSala';
  }
}

/** Espera máxima por defecto de una petición de la Sala: el servidor puede estar esperando a Medplum (hasta 20 s), y el médico no debe quedar colgado. */
export const TIEMPO_MAX_MS = 15_000;
/** «Iniciar nueva consulta» desde la ficha: más corto, porque la ficha puede seguir sola sin la Sala (nunca se bloquea la atención). */
export const TIEMPO_MAX_INICIAR_PACIENTE_MS = 8_000;

export async function pedir<T>(metodo: 'GET' | 'POST' | 'PUT', ruta: string, cuerpo?: unknown, tiempoMaxMs = TIEMPO_MAX_MS): Promise<T> {
  let res: Response;
  try {
    res = await fetch(vaultUrl(ruta), {
      method: metodo,
      headers: cabecerasBoveda(),
      signal: AbortSignal.timeout(tiempoMaxMs),
      ...(cuerpo !== undefined ? { body: JSON.stringify(cuerpo) } : {}),
    });
  } catch (err) {
    if (err instanceof DOMException && (err.name === 'TimeoutError' || err.name === 'AbortError')) {
      throw new ErrorSala('El servidor tardó demasiado en responder. Revisa la Sala antes de repetir la acción.', 0, 'tiempo_agotado');
    }
    throw new ErrorSala('No hay conexión con el servidor.', 0, 'sin_conexion');
  }
  const datos = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new ErrorSala(
      typeof datos.error === 'string' && datos.error ? datos.error : 'No se pudo completar la acción.',
      res.status,
      typeof datos.codigo === 'string' ? datos.codigo : 'error',
      typeof datos.estadoActual === 'string' ? (datos.estadoActual as EstadoTurno) : undefined,
    );
  }
  return datos as T;
}

export const obtenerSala = (): Promise<SalaDelDia> => pedir('GET', '/api/hce/sala');
export const obtenerResumenSala = (): Promise<ResumenSala> => pedir('GET', '/api/hce/sala/resumen');
export const llamarTurno = (id: number, estadoEsperado: EstadoTurno): Promise<ResultadoLlamar> => pedir('POST', `/api/hce/sala/${id}/llamar`, { estadoEsperado });
export const iniciarTurno = (id: number, estadoEsperado: EstadoTurno): Promise<ResultadoIniciar> => pedir('POST', `/api/hce/sala/${id}/iniciar`, { estadoEsperado });
export const finalizarTurno = (id: number, estadoEsperado: EstadoTurno, confirmar = false): Promise<ResultadoFinalizar> =>
  pedir('POST', `/api/hce/sala/${id}/finalizar`, { estadoEsperado, ...(confirmar ? { confirmar: true } : {}) });
/** «Asistió» del médico sobre una cita de hoy suya que aún no llegó: crea el turno (como recepción). */
export const marcarAsistio = (citaId: string): Promise<{ ticketId: number | null; turno: string | null; estado: EstadoTurno | null; creado: boolean }> =>
  pedir('POST', '/api/hce/sala/asistio', { citaId });
/** «Deshacer asistencia» de un turno propio que sigue esperando: el turno se retira y la cita vuelve a Programada. */
export const deshacerTurno = (id: number, estadoEsperado: EstadoTurno): Promise<{ estado: 'retirado' }> => pedir('POST', `/api/hce/sala/${id}/deshacer`, { estadoEsperado });
export const noAsistioTurno = (id: number, estadoEsperado: EstadoTurno): Promise<{ estado: 'no_show' }> => pedir('POST', `/api/hce/sala/${id}/no-asistio`, { estadoEsperado });
export const fijarConsultorioDelDia = (consultorio: string): Promise<{ efectivo: string | null; habitual: string | null; delDia: string | null }> =>
  pedir('PUT', '/api/hce/sala/consultorio', { consultorio });
export const iniciarConsultaDePaciente = (medplumPatientId: string, confirmarToma = false): Promise<ResultadoIniciarPaciente> =>
  pedir('POST', '/api/hce/sala/iniciar-paciente', { medplumPatientId, ...(confirmarToma ? { confirmarToma: true } : {}) }, TIEMPO_MAX_INICIAR_PACIENTE_MS);

/**
 * Aviso de la Sala para el médico (rebanada 5c): hoy solo «turno cerrado automáticamente, revisar». El servidor lo acota por la sesión (consultorio y
 * Practitioner): un médico solo recibe los suyos. `encounterId` es el id pelado del Encounter que abrió «Iniciar consulta» (puede faltar).
 */
export type AvisoSala = {
  id: number;
  tipo: 'cierre_automatico_revisar';
  ticketId: number;
  citaId: string | null;
  encounterId: string | null;
  turno: string | null;
  paciente: string;
  fechaTurno: string;
  creadaEn: string;
};

export const obtenerAvisosSala = (): Promise<{ alertas: AvisoSala[] }> => pedir('GET', '/api/hce/sala-alertas');
export const marcarAvisoRevisado = (id: number): Promise<{ ok: true; ya: boolean }> => pedir('POST', `/api/hce/sala-alertas/${id}/resolver`);

// --- Lógica pura (se prueba sin red ni DOM) -----------------------------------------------------------------------------------------------

/** Qué botones ofrece una fila (el servidor lo vuelve a exigir). La vista del admin es de solo lectura: ninguno. */
export type AccionFila = 'llamar' | 'volverALlamar' | 'iniciar' | 'noAsistio' | 'deshacer' | 'abrirHistoria' | 'reintentarHistoria' | 'finalizar' | 'cerrarConsulta';

const ACCIONES_DE_CONSULTA: readonly AccionFila[] = ['iniciar', 'abrirHistoria', 'reintentarHistoria', 'finalizar', 'cerrarConsulta'];

/** `puedeConsultar = false` (enfermería) quita las acciones que abren o cierran la consulta: llamar y «no asistió» siguen. */
export function accionesDeFila(
  f: Pick<FilaSala, 'estado' | 'llamados' | 'maxLlamados' | 'consultaAbierta' | 'sinEncounter' | 'esMio'> & Partial<Pick<FilaSala, 'modalidad' | 'puedeDeshacer'>>,
  soloLectura = false,
  puedeConsultar = true,
): AccionFila[] {
  // Una teleconsulta se atiende con Conectar/Finalizar de telemedicina (lógica propia): la Sala presencial no ofrece nada sobre ella.
  if (soloLectura || !f.esMio || f.modalidad === 'telemedicina') return [];
  let acciones: AccionFila[];
  switch (f.estado) {
    case 'waiting':
      acciones = ['llamar', 'iniciar', 'noAsistio', ...(f.puedeDeshacer ? (['deshacer'] as const) : [])];
      break;
    case 'called':
      acciones = [...(f.llamados < f.maxLlamados ? (['volverALlamar'] as const) : []), 'iniciar', 'noAsistio'];
      break;
    case 'in_consult':
      acciones = [f.sinEncounter ? 'reintentarHistoria' : 'abrirHistoria', 'finalizar'];
      break;
    case 'done':
      acciones = f.consultaAbierta ? ['cerrarConsulta'] : [];
      break;
    default:
      acciones = [];
  }
  return puedeConsultar ? acciones : acciones.filter((a) => !ACCIONES_DE_CONSULTA.includes(a));
}

/**
 * Segundos de espera para mostrar: lo que dijo el servidor + el tiempo transcurrido DESDE que se recibió la respuesta (`recibidoEnMs` y `ahoraMs`
 * son lecturas del mismo reloj monotónico, p. ej. `performance.now()`). Nunca depende de la hora de pared del equipo del médico.
 */
export function esperaActualSeg(esperaSeg: number | null, recibidoEnMs: number, ahoraMs: number): number | null {
  if (esperaSeg === null) return null;
  return Math.max(0, esperaSeg + Math.floor(Math.max(0, ahoraMs - recibidoEnMs) / 1000));
}

/** `menos de 1 min`, `7 min`, `1 h 05 min`. */
export function esperaLegible(seg: number): string {
  const min = Math.floor(seg / 60);
  if (min < 1) return 'menos de 1 min';
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')} min`;
}

/**
 * Pacientes en espera que NO se habían visto antes (para el aviso «X llegó»). `vistos === null` es la primera carga: no avisa de nadie (el médico
 * acaba de abrir la pantalla; lo que ya esperaba no es una llegada nueva). Solo cuentan los que están `waiting` (un llamado ya se vio).
 */
export function llegadasNuevas(vistos: ReadonlySet<number> | null, resumen: ResumenSala): ResumenSala['enEspera'] {
  if (vistos === null) return [];
  return resumen.enEspera.filter((e) => e.estado === 'waiting' && !vistos.has(e.id));
}

/** Id del paciente (`Patient/<id>`) de un Encounter leído de Medplum, o `null` si no lo trae. */
export function pacienteDeEncounter(encounter: { subject?: { reference?: string } } | null | undefined): string | null {
  const m = /^Patient\/(.+)$/.exec(encounter?.subject?.reference ?? '');
  return m?.[1] ?? null;
}

/** Ficha del paciente del aviso, en Notas (igual que «Iniciar consulta» de la Sala). No lleva `?encounter=`: esa pantalla no lo lee. */
export function rutaHistoriaDeAviso(patientId: string): string {
  return `/${encodeURIComponent(patientId)}/notas`;
}

/** `DD/MM` de un instante según el calendario de Ecuador (UTC-5 fijo; no depende del idioma ni de la zona del equipo). */
export function fechaCortaEcuador(iso: string): string {
  const g = new Date(new Date(iso).getTime() - 5 * 3_600_000);
  return `${String(g.getUTCDate()).padStart(2, '0')}/${String(g.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** Hora de la cita (`HH:MM`) en el reloj de Ecuador, sin depender de la zona horaria del equipo. */
export function horaEcuadorDe(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Guayaquil' });
}

// --- Avisos «X llegó» ya mostrados (compartido entre pestañas: una llegada se avisa una sola vez, aunque la pestaña que la ve primero esté oculta) ---

const CLAVE_AVISADOS = 'lc-sala-avisados';
const MAX_AVISADOS = 100;

export function leerAvisados(almacen: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage): Set<number> {
  try {
    const crudo = almacen?.getItem(CLAVE_AVISADOS);
    const lista = crudo ? (JSON.parse(crudo) as unknown) : [];
    return new Set(Array.isArray(lista) ? lista.filter((x): x is number => typeof x === 'number') : []);
  } catch {
    return new Set();
  }
}

export function marcarAvisados(ids: readonly number[], almacen: Pick<Storage, 'getItem' | 'setItem'> | null = typeof localStorage === 'undefined' ? null : localStorage): void {
  try {
    const todos = [...leerAvisados(almacen), ...ids];
    almacen?.setItem(CLAVE_AVISADOS, JSON.stringify(todos.slice(-MAX_AVISADOS)));
  } catch {
    // sin almacenamiento (modo privado): el aviso se repetiría como mucho una vez por pestaña, nunca se pierde
  }
}

/** Una llegada muy vieja (la pestaña estuvo oculta horas) ya no es noticia: no se avisa. */
export const VIGENCIA_AVISO_MS = 15 * 60_000;

export function esLlegadaVigente(llegadaAtIso: string, ahoraServidorIso: string): boolean {
  const dif = new Date(ahoraServidorIso).getTime() - new Date(llegadaAtIso).getTime();
  return Number.isFinite(dif) && dif <= VIGENCIA_AVISO_MS;
}

// --- Contador de espera del menú (almacén de módulo: lo escribe el aviso global, lo lee el menú de cualquier pantalla) -------------------------

export type EstadoContadorSala = { disponible: boolean; esperando: number };
let estadoContador: EstadoContadorSala = { disponible: false, esperando: 0 };
const oyentesContador = new Set<() => void>();

export function publicarContadorSala(nuevo: EstadoContadorSala): void {
  if (nuevo.disponible === estadoContador.disponible && nuevo.esperando === estadoContador.esperando) return;
  estadoContador = nuevo;
  for (const fn of oyentesContador) fn();
}
export const suscribirContadorSala = (fn: () => void): (() => void) => {
  oyentesContador.add(fn);
  return () => oyentesContador.delete(fn);
};
export const leerContadorSala = (): EstadoContadorSala => estadoContador;
